import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useClosingTradesStore } from '@ui/features/Websocket/useClosingTradesStore';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import type { Address } from '@ui/types/alias';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useInvalidatePositionQuery } from '../../hooks/useInvalidateQuery';

export const useBatchCloseTrade = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  const invalidatePositionQuery = useInvalidatePositionQuery();
  const addClosingTrade = useClosingTradesStore((s) => s.addClosingTrade);
  const mutationToastInfo = useMutationToast();
  const chainId = useDataChainId();
  const closeTradeHashesRef = useRef<Address[]>([]);

  const {
    isReady: canUseOneClickTradingV2,
    sendBatchClosePosition: sendBatchClosePositionV2,
  } = useOneClickTradingV2();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  const { writeContract: writeContractOnchain, isPending } = useTxnWriteContract({
    onSuccess: () => {
      closeTradeHashesRef.current.forEach((tradeHash) => {
        addClosingTrade(tradeHash, chainId);
      });
      invalidatePositionQuery();
      onSuccess?.();
    },
  });

  const handleOneClickBatchCloseV2 = useCallback(
    async (tradeHashes: Address[], broker?: number) => {
      if (!canUseOneClickTradingV2) return;

      setIsAwaitingOneClickResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 batch close in progress',
      });

      try {
        const intentHash = await sendBatchClosePositionV2(tradeHashes, broker);

        const status = await pollV2IntentStatus(intentHash);
        if (!status.success) {
          throw new Error(
            status.reason || 'One-click V2 batch close execution failed',
          );
        }

        tradeHashes.forEach((tradeHash) => {
          addClosingTrade(tradeHash, chainId);
        });
        invalidatePositionQuery();

        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Positions batch closed via one-click V2',
          txHash: status.txnHash as Address,
        });

        onSuccess?.();
        return status.txnHash;
      } catch (error) {
        mutationToastInfo.onError({
          errorMsg:
            error instanceof Error
              ? extractErrorMessage(error.message) ?? error.message
              : undefined,
          onErrorToastOpts: {
            description: 'One-click V2 batch close failed, please try again',
          },
        });
        throw error;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickResult(false);
      }
    },
    [
      addClosingTrade,
      canUseOneClickTradingV2,
      chainId,
      closeTradeHashesRef,
      invalidatePositionQuery,
      mutationToastInfo,
      onSuccess,
      sendBatchClosePositionV2,
    ],
  );

  const batchCloseTrade = useCallback(
    (tradeHashes: Address[], broker?: number) => {
      // OneClick V2 path (priority)
      if (canUseOneClickTradingV2) {
        return handleOneClickBatchCloseV2(tradeHashes, broker);
      }
      const address = getContractAddress('LeverUp', chainId);
      if (!address) {
        throw new Error('leverup contract address not found');
      }

      closeTradeHashesRef.current = tradeHashes;

      return writeContractOnchain({
        abi: TradingPortalFacet,
        address,
        functionName: 'batchCloseTrade',
        args: broker === undefined ? [tradeHashes] : [tradeHashes, broker],
      });
    },
    [chainId, writeContractOnchain, canUseOneClickTradingV2, handleOneClickBatchCloseV2],
  );

  const isClosePending =
    isPending || isAwaitingOneClickResult;

  return useMemo(
    () => ({
      isPending: isClosePending,
      writeContract: batchCloseTrade,
    }),
    [batchCloseTrade, isClosePending],
  );
};

